/**
 * ReleaseContext.tsx — die global gewählte Formatversion (FV-Stand).
 *
 * Eine Auswahl im Kopf der App, die auf alle Sichten durchschlägt: Startseite,
 * Artefakte, Regelwerk, Prüfid-Viewer. Gehalten wird ausschließlich die
 * **FV-Achse** (`202604`, `202610`, `202710`, …). Die zweite Achse — der
 * *Stand* (Ausgabe innerhalb einer FV, siehe `TabellenPage`) — bleibt bewusst
 * lokal; global wäre sie unbedienbar.
 *
 * ── Vertrag für die Verbraucher (Welle 2) ──────────────────────────────────
 *
 *   const { release, setRelease, releases } = useRelease();
 *      release   ReleaseKey   aktuelle Wahl, `ALL` ("all") = „Alle Stände“
 *      setRelease(r)           setzt + persistiert die Wahl
 *      releases  ReleaseKey[]  bekannte FV-Stände, chronologisch, OHNE `ALL`
 *
 *   useSyncReleaseFromRoute(routeRelease)
 *      Deep-Link-Vorrang. Trägt die Route bereits einen Stand
 *      (`/artifacts/:type/:release`, `/pruefid/:release`, `?fv=…`), reicht die
 *      Seite ihn hier hinein — die Route gewinnt und **setzt den globalen
 *      Stand nach**, nicht umgekehrt. `undefined`/`null`/unbekannter Wert =
 *      kein Eingriff, die globale Wahl bleibt stehen. Die Seite rendert
 *      weiterhin gegen ihren eigenen Routen-Wert; der Kontext zieht nach.
 *
 *   matchesRelease(release, kandidat)
 *      Filterprädikat. `ALL` lässt alles durch; ein Kandidat ohne FV-Bezug
 *      (`null`/`undefined` — z. B. `bnetza-festlegung`, `bo4e-schema`) bleibt
 *      immer sichtbar, denn er liegt gar nicht auf der FV-Achse.
 *
 *   releaseLabel / releaseShortLabel   Beschriftungen für Kopf und Fließtext.
 *
 * Persistenz in `localStorage` unter `maco-status-release`, gleiche Machart
 * wie `maco-status-theme` in `src/layout/ThemeContext.tsx`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import artifactsJson from "@/data/generated/artifacts.json";
import pipelineJson from "@/data/generated/pipeline.json";
import { ALL, type ReleaseKey } from "@/types/pipeline";

export const RELEASE_STORAGE_KEY = "maco-status-release";

/** Ein FV-Stand ist sechsstellig: Jahr + Monat ("202610"). */
const FV_PATTERN = /^\d{6}$/;

// ---------------------------------------------------------------------------
// Stände aus den Daten ableiten — keine hartkodierte Liste
// ---------------------------------------------------------------------------

/**
 * Vereinigt die Stände aus `pipeline.json` (`releases`) und die Schlüssel der
 * Artefakt-Zählungen aus `artifacts.json` (`counts[typ]`). Beides sind
 * generierte Dateien; eine Konstante im Code wäre genau die Drift, die dieses
 * Projekt gerade überall beseitigt.
 *
 * Nicht jeder `counts`-Schlüssel ist eine Formatversion: `bnetza-festlegung`
 * zählt nach `GPKE`/`WiM`/`MaBiS`/…, `bo4e-schema` nach `unbefristet`. Nur
 * sechsstellige Schlüssel liegen auf der FV-Achse.
 */
export function deriveReleases(
  pipelineReleases: readonly string[] | undefined,
  counts: Record<string, Record<string, unknown>> | undefined,
): ReleaseKey[] {
  const found = new Set<string>();
  for (const r of pipelineReleases ?? []) {
    if (FV_PATTERN.test(r)) found.add(r);
  }
  for (const byRelease of Object.values(counts ?? {})) {
    for (const r of Object.keys(byRelease ?? {})) {
      if (FV_PATTERN.test(r)) found.add(r);
    }
  }
  // Chronologisch — die Kopfzeile liest sich wie eine Zeitachse.
  return [...found].sort();
}

const pipeline = pipelineJson as { releases?: string[] };
const artifacts = artifactsJson as {
  counts?: Record<string, Record<string, unknown>>;
};

/** Die bekannten FV-Stände dieses Builds (ohne `ALL`). */
export const RELEASES: ReleaseKey[] = deriveReleases(
  pipeline.releases,
  artifacts.counts,
);

// ---------------------------------------------------------------------------
// Beschriftung
// ---------------------------------------------------------------------------

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/** `"202610"` → `"Oktober 2026 (FV2610)"`, `ALL` → `"Alle Stände"`. */
export function releaseLabel(release: ReleaseKey): string {
  if (release === ALL) return "Alle Stände";
  const m = /^(\d{4})(\d{2})$/.exec(release);
  if (!m) return release;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]} (FV${release.slice(2)})`;
}

/** `"202610"` → `"FV2610"`, `ALL` → `"Alle"`. Für enge Stellen (Kopfzeile). */
export function releaseShortLabel(release: ReleaseKey): string {
  if (release === ALL) return "Alle";
  return FV_PATTERN.test(release) ? `FV${release.slice(2)}` : release;
}

// ---------------------------------------------------------------------------
// Filterprädikat
// ---------------------------------------------------------------------------

/**
 * Gehört `candidate` zur Auswahl `release`?
 *
 * - `release === ALL` → alles.
 * - `candidate == null` → immer sichtbar: der Eintrag liegt nicht auf der
 *   FV-Achse (BNetzA-Festlegungen, BO4E-Schemas) und darf bei einer FV-Wahl
 *   nicht verschwinden.
 */
export function matchesRelease(
  release: ReleaseKey,
  candidate: string | null | undefined,
): boolean {
  if (release === ALL) return true;
  if (candidate == null || candidate === "") return true;
  return candidate === release;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface ReleaseContextValue {
  /** Aktuelle Wahl; `ALL` bedeutet „Alle Stände“. */
  release: ReleaseKey;
  /** Setzt die Wahl (unbekannte Werte werden ignoriert) und persistiert sie. */
  setRelease: (release: ReleaseKey) => void;
  /** Bekannte FV-Stände, chronologisch aufsteigend, ohne `ALL`. */
  releases: ReleaseKey[];
}

const ReleaseCtx = createContext<ReleaseContextValue | undefined>(undefined);

function readStored(valid: Set<string>): ReleaseKey {
  if (typeof window === "undefined") return ALL;
  try {
    const stored = window.localStorage.getItem(RELEASE_STORAGE_KEY);
    // Ein Stand, den dieser Build nicht mehr kennt (alter Eintrag im Browser),
    // fällt still auf „Alle“ zurück — nie auf eine leere Oberfläche.
    return stored && valid.has(stored) ? stored : ALL;
  } catch {
    return ALL;
  }
}

export function ReleaseProvider({
  children,
  releases: releasesProp,
}: {
  children: ReactNode;
  /** Nur für Tests/Storybook — sonst kommen die Stände aus den Daten. */
  releases?: ReleaseKey[];
}) {
  const releases = useMemo(() => releasesProp ?? RELEASES, [releasesProp]);
  const valid = useMemo(() => new Set<string>([ALL, ...releases]), [releases]);

  const [release, setReleaseState] = useState<ReleaseKey>(() => readStored(valid));

  // Wechselt der Datenstand (neuer Build ohne die gemerkte FV), wird die Wahl
  // auf „Alle“ zurückgeholt statt eine leere Sicht zu zeigen.
  useEffect(() => {
    if (!valid.has(release)) setReleaseState(ALL);
  }, [valid, release]);

  useEffect(() => {
    try {
      window.localStorage.setItem(RELEASE_STORAGE_KEY, release);
    } catch {
      /* Private-Mode / gesperrter Storage — die Wahl gilt dann nur zur Laufzeit. */
    }
  }, [release]);

  // Identität stabil über Wahl-Wechsel hinweg: `useSyncReleaseFromRoute` hängt
  // an `setRelease`. Würde der Setter bei jeder Auswahl neu entstehen, liefe
  // der Routen-Effekt erneut und zöge die Wahl im Kopf sofort wieder zurück.
  const setRelease = useCallback(
    (next: ReleaseKey) => {
      setReleaseState((cur) => (valid.has(next) ? next : cur));
    },
    [valid],
  );

  const value = useMemo<ReleaseContextValue>(
    () => ({ release, setRelease, releases }),
    [release, setRelease, releases],
  );

  return <ReleaseCtx.Provider value={value}>{children}</ReleaseCtx.Provider>;
}

export function useRelease(): ReleaseContextValue {
  const ctx = useContext(ReleaseCtx);
  if (!ctx) throw new Error("useRelease must be used within ReleaseProvider");
  return ctx;
}

/**
 * Deep-Link-Vorrang: Die Route trägt bereits einen Stand → sie gewinnt und
 * **setzt den globalen Stand nach**.
 *
 * Aufruf in der Seite, direkt neben dem Auslesen des Parameters:
 *
 * ```tsx
 * const { release: routeRelease } = useParams<{ release?: string }>();
 * useSyncReleaseFromRoute(routeRelease);          // Route → global
 * const { release } = useRelease();               // global → Fallback ohne Param
 * const effective = routeRelease ?? release;
 * ```
 *
 * `undefined`, `null`, `""` und Werte, die dieser Build nicht kennt, lassen die
 * globale Wahl unangetastet — ein toter Lesezeichen-Link kippt niemandem den
 * Kopf der App um.
 */
export function useSyncReleaseFromRoute(
  routeRelease: string | null | undefined,
): void {
  const { setRelease, releases } = useRelease();

  // Abhängig allein von der Route: Wählt jemand danach im Kopf einen anderen
  // Stand, läuft dieser Effekt nicht erneut — sonst zöge die Route die Auswahl
  // bei jedem Klick zurück.
  useEffect(() => {
    if (!routeRelease) return;
    if (routeRelease !== ALL && !releases.includes(routeRelease)) return;
    setRelease(routeRelease);
  }, [routeRelease, releases, setRelease]);
}
